import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { VideoJob, VideoJobStatus } from '../types/videoJob';
import { getCached, setCached, shouldRevalidate } from '../utils/sessionCache';

export function useVideoJob(jobId: string | null) {
  const [job, setJob] = useState<VideoJob | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!jobId) {
      setJob(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    let pollInterval: NodeJS.Timeout | null = null;

    async function fetchJob() {
      try {
        const cacheKey = `video_job_${jobId}`;
        const cached = getCached<VideoJob>(cacheKey);
        
        // If cached and job is in final state, use cache
        if (cached && (cached.status === 'ready' || cached.status === 'failed')) {
          setJob(cached);
          setLoading(false);
          return;
        }
        
        // If cached and recent, use cache but check if revalidation needed
        if (cached && !shouldRevalidate(cacheKey)) {
          setJob(cached);
          setLoading(false);
          
          // If processing, continue polling
          if (cached.status === 'processing' || cached.status === 'uploaded') {
            pollInterval = setTimeout(fetchJob, 5000); // Poll every 5 seconds
          }
          return;
        }
        
        // Fetch from Firestore
        const jobRef = doc(db, 'video_jobs', jobId);
        const snapshot = await getDoc(jobRef);
        
        if (snapshot.exists()) {
          const data = snapshot.data() as VideoJob;
          setJob(data);
          setCached(cacheKey, data);
          
          // Continue polling if processing
          if (data.status === 'processing' || data.status === 'uploaded') {
            pollInterval = setTimeout(fetchJob, 5000);
          }
        } else {
          setJob(null);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error fetching video job:', error);
        setLoading(false);
      }
    }

    fetchJob();

    return () => {
      if (pollInterval) clearTimeout(pollInterval);
    };
  }, [jobId]);

  return { job, loading };
}

export function getJobStatusMessage(status: VideoJobStatus): string {
  switch (status) {
    case 'uploaded':
      return 'กำลังเตรียมวิดีโอให้เหมาะสมกับการเรียน...';
    case 'processing':
      return 'กำลังเตรียมวิดีโอให้เหมาะสมกับการเรียน...';
    case 'ready':
      return 'วิดีโอพร้อมใช้งาน ✅';
    case 'failed':
      return 'เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง';
    default:
      return 'กำลังประมวลผล...';
  }
}
